/* cspell:words Approvable */

import {
  TagLayerValues,
  TAXONOMY_PRODUCT_POLICY_BRIEF,
  type ICanonicalHierarchyAwaitingApprovalExportProposal,
  type ICanonicalBootstrapAwaitingApprovalExportProposal,
  type ICanonicalTagProposal,
  type ICanonicalTag,
} from "@monorepo/core";

export type CanonicalProposalDraft = {
  layer: string;
  slug: string;
  name: string;
  description: string;
  synonyms: string;
  rejectionNotes: string;
};

export type CanonicalProposalApprovalDraft = Pick<
  ICanonicalTag,
  "layer" | "slug" | "name" | "description" | "synonyms" | "exampleOracleIds"
>;

export interface AwaitingApprovalReviewExportFile {
  fileName: string;
  content: string;
}

const MAX_PROPOSALS_PER_REVIEW_MARKDOWN = 40;
const MAX_HIERARCHY_PROPOSALS_PER_REVIEW_MARKDOWN = 30;

const toCommaSeparatedValue = (items: readonly string[]) => items.join(", ");

const SHARED_TAXONOMY_REVIEW_RULES_SECTION = `## Shared Canonical Taxonomy Rules

Apply the same taxonomy rules used during proposal generation:

${TAXONOMY_PRODUCT_POLICY_BRIEF}`;

export const createProposalDraft = (
  proposal: ICanonicalTagProposal,
): CanonicalProposalDraft => ({
  layer: proposal.layer ?? "FUNCTIONAL",
  slug: proposal.slug ?? "",
  name: proposal.name ?? "",
  description: proposal.description ?? "",
  synonyms: toCommaSeparatedValue(proposal.synonyms),
  rejectionNotes: "",
});

export const parseCommaSeparatedList = (value: string) =>
  value
    .split(",")
    .map((part) => part.trim())
    .filter((part): part is string => part.length > 0);

export const isCanonicalProposalDraftApprovable = (
  draft: CanonicalProposalDraft,
) =>
  TagLayerValues.includes(draft.layer as (typeof TagLayerValues)[number]) &&
  draft.slug.trim().length > 0 &&
  draft.name.trim().length > 0 &&
  draft.description.trim().length > 0;

export const buildApprovedTagDraft = ({
  proposal,
  draft,
}: {
  proposal: ICanonicalTagProposal;
  draft: CanonicalProposalDraft;
}): CanonicalProposalApprovalDraft => ({
  layer: draft.layer as (typeof TagLayerValues)[number],
  slug: draft.slug.trim(),
  name: draft.name.trim(),
  description: draft.description.trim(),
  synonyms: parseCommaSeparatedList(draft.synonyms),
  exampleOracleIds: proposal.exampleOracleIds,
});

const normalizeSearchValue = (value: string) =>
  value.trim().toLocaleLowerCase();

export const canonicalProposalMatchesSearch = ({
  proposal,
  draft,
  query,
}: {
  proposal: ICanonicalTagProposal;
  draft: CanonicalProposalDraft;
  query: string;
}) => {
  const normalizedQuery = normalizeSearchValue(query);
  if (normalizedQuery.length === 0) {
    return true;
  }

  const haystacks = [
    proposal.proposalId,
    proposal.runId,
    proposal.clusterKey,
    proposal.clusterLabel,
    proposal.reasoningSummary,
    proposal.sourceModel ?? "",
    proposal.layer ?? "",
    proposal.slug ?? "",
    proposal.name ?? "",
    proposal.description ?? "",
    draft.layer,
    draft.slug,
    draft.name,
    draft.description,
    draft.synonyms,
    ...proposal.synonyms,
    ...proposal.exampleOracleIds,
    ...proposal.sourceTagKeys.map((sourceTagKey) => sourceTagKey.rawKey),
  ];

  return haystacks.some((value) =>
    normalizeSearchValue(value).includes(normalizedQuery),
  );
};

const formatMarkdownItems = (items: readonly string[]) =>
  items.length > 0
    ? items.map((item) => `  - ${item}`).join("\n")
    : "  - (none)";

const normalizePromptText = (value: string) => value.trim().toLocaleLowerCase();

const countPromptTokens = (value: string) =>
  normalizePromptText(value)
    .split(/\s+/)
    .filter((token) => token.length > 0).length;

const buildHighSignalClusterKeywords = ({
  clusterLabel,
  sourceRawTags,
  clusterKeywords,
}: {
  clusterLabel: string;
  sourceRawTags: readonly string[];
  clusterKeywords: readonly string[];
}) => {
  const normalizedClusterLabel = normalizePromptText(clusterLabel);
  const normalizedSourceRawTags = new Set(
    sourceRawTags.map((tag) => normalizePromptText(tag)),
  );

  const prioritizedKeywords = clusterKeywords.filter((keyword) => {
    const normalizedKeyword = normalizePromptText(keyword);
    const tokenCount = countPromptTokens(keyword);

    return (
      tokenCount >= 2 ||
      normalizedKeyword === normalizedClusterLabel ||
      normalizedSourceRawTags.has(normalizedKeyword)
    );
  });

  const selectedKeywords =
    prioritizedKeywords.length > 0 ? prioritizedKeywords : clusterKeywords;

  return selectedKeywords.slice(0, 4);
};

const buildRepresentativeMechanicSnippets = (
  snippets: readonly { cardLabel: string; text: string }[],
) => {
  const dedupedSnippets = snippets.filter(
    (snippet, index, array) =>
      array.findIndex(
        (candidate) =>
          candidate.cardLabel === snippet.cardLabel &&
          candidate.text === snippet.text,
      ) === index,
  );

  const rankedSnippets = dedupedSnippets.slice().sort((left, right) => {
    const tokenDelta =
      countPromptTokens(right.text) - countPromptTokens(left.text);
    if (tokenDelta !== 0) {
      return tokenDelta;
    }

    return left.cardLabel.localeCompare(right.cardLabel);
  });

  const selectedSnippets: typeof rankedSnippets = [];
  const snippetCountByCard = new Map<string, number>();

  for (const snippet of rankedSnippets) {
    const currentCardSnippetCount =
      snippetCountByCard.get(snippet.cardLabel) ?? 0;
    if (currentCardSnippetCount >= 1 && selectedSnippets.length < 3) {
      continue;
    }

    selectedSnippets.push(snippet);
    snippetCountByCard.set(snippet.cardLabel, currentCardSnippetCount + 1);

    if (selectedSnippets.length >= 4) {
      break;
    }
  }

  return selectedSnippets;
};

const formatPromptContextSection = (
  proposalItem: ICanonicalBootstrapAwaitingApprovalExportProposal,
) => {
  const { promptContext } = proposalItem;
  const sourceRawTags = proposalItem.proposal.sourceTagKeys.map(
    (sourceTagKey) => sourceTagKey.rawKey,
  );

  if (promptContext.mode === "derived") {
    const highSignalClusterKeywords = buildHighSignalClusterKeywords({
      clusterLabel: proposalItem.proposal.clusterLabel,
      sourceRawTags,
      clusterKeywords: promptContext.clusterKeywords,
    });
    const representativeMechanicSnippets = buildRepresentativeMechanicSnippets(
      promptContext.mechanicSnippets,
    );

    return `- High-signal cluster keywords:
${formatMarkdownItems(highSignalClusterKeywords)}
- Representative mechanic snippets:
${formatMarkdownItems(
  representativeMechanicSnippets.map(
    (snippet) => `${snippet.cardLabel}: "${snippet.text}"`,
  ),
)}`;
  }

  return `- Exemplar card full text:
${formatMarkdownItems(
  promptContext.fullTextCards.length > 0
    ? promptContext.fullTextCards.map(
        (card) => `${card.cardLabel}: "${card.oracleText}"`,
      )
    : ["No stored exemplar Oracle text was available for this proposal."],
)}`;
};

export const buildAwaitingApprovalReviewMarkdown = ({
  runId,
  items,
  drafts,
  partIndex,
  partCount,
  totalItemCount,
}: {
  runId?: string;
  items: readonly ICanonicalBootstrapAwaitingApprovalExportProposal[];
  drafts: Readonly<Record<string, CanonicalProposalDraft>>;
  partIndex?: number;
  partCount?: number;
  totalItemCount?: number;
}) => {
  const generatedAt = new Date().toISOString();
  const isChunkedExport =
    typeof partIndex === "number" &&
    typeof partCount === "number" &&
    partCount > 1;
  const proposalSections = items
    .map((item, index) => {
      const { proposal, supportingCards } = item;
      const draft =
        drafts[proposal.proposalId] ?? createProposalDraft(proposal);

      return `## Proposal ${index + 1}: ${proposal.proposalId}

- Cluster label: ${proposal.clusterLabel}
- Proposed layer: ${draft.layer}
- Proposed slug: ${draft.slug || "(empty)"}
- Proposed name: ${draft.name || "(empty)"}
- Proposed description: ${draft.description || "(empty)"}
- Synonyms:
${formatMarkdownItems(parseCommaSeparatedList(draft.synonyms))}
- Source raw tags:
${formatMarkdownItems(proposal.sourceTagKeys.map((sourceTagKey) => sourceTagKey.rawKey))}
${formatPromptContextSection({
  ...item,
  supportingCards,
})}`;
    })
    .join("\n\n");

  return `# Canonical Bootstrap Awaiting-Approval Review

You are reviewing canonical tag proposals that are still awaiting approval for bootstrap run \`${runId ?? "unknown"}\`.

## Required context

Before reviewing any proposal, read and internalize these repository documents:

- \`docs/product-design.md\`
- \`docs/system-architecture.md\`

Use those documents as governing context for product intent, taxonomy behavior, system constraints, and approval standards.

${SHARED_TAXONOMY_REVIEW_RULES_SECTION}

## Goal

Identify whether any of the awaiting-approval proposals should be denied rather than approved.

## Guidance

Approve by default if a proposal is coherent, product-useful, and consistent with the source tags and exemplar cards.

Only deny a proposal when there is a meaningful problem such as:

- the concept is too noisy or not really a user-facing canonical tag
- the proposal duplicates another proposal conceptually
- the name or slug is misleading
- the proposal is placed in the wrong layer
- the description does not match the source tags

## Required response

- Reply with \`APPROVE_ALL\` if none of the proposals should be denied.
- Otherwise, reply with a markdown table containing:
  - \`proposalId\`
  - \`deny\`
  - \`reason\`
- Only set \`deny\` to \`yes\` for proposals that should be rejected.
- Keep proposals out of the table if they should remain approved.

Generated at: ${generatedAt}
Pending proposal count in this file: ${items.length}
${isChunkedExport ? `Total pending proposal count across all files: ${totalItemCount ?? items.length}` : ""}
${isChunkedExport ? `Export part: ${partIndex} of ${partCount}` : ""}

${proposalSections}`;
};

export const buildHierarchyAwaitingApprovalReviewMarkdown = ({
  runId,
  hierarchyRunId,
  items,
  partIndex,
  partCount,
  totalItemCount,
}: {
  runId?: string;
  hierarchyRunId?: string;
  items: readonly ICanonicalHierarchyAwaitingApprovalExportProposal[];
  partIndex?: number;
  partCount?: number;
  totalItemCount?: number;
}) => {
  const generatedAt = new Date().toISOString();
  const isChunkedExport =
    typeof partIndex === "number" &&
    typeof partCount === "number" &&
    partCount > 1;
  const proposalSections = items
    .map((item, index) => {
      const { proposal } = item;

      const promptContextSection = proposal.promptContext
        ? formatPromptContextSection({
            proposal: {
              proposalId: proposal.targetProposalId,
              runId: proposal.runId,
              clusterKey: proposal.targetTag.slug,
              clusterLabel: proposal.targetTag.name,
              proposalSource: "LLM",
              sourceModel: proposal.sourceModel,
              sourceTagKeys: proposal.sourceTagKeys,
              layer: proposal.targetTag.layer,
              slug: proposal.targetTag.slug,
              name: proposal.targetTag.name,
              description: proposal.targetTag.description,
              parentSlugs: proposal.targetTag.parentSlugs,
              synonyms: proposal.targetTag.synonyms,
              exampleOracleIds: proposal.targetTag.exampleOracleIds,
              confidence: proposal.confidence,
              needsReview: true,
              reasoningSummary: proposal.reasoningSummary,
              proposalStatus: "PENDING",
              createdAt: proposal.createdAt,
              updatedAt: proposal.updatedAt,
            },
            promptContext: proposal.promptContext,
            supportingCards: [],
          })
        : "- Mechanics context:\n  - No stored mechanics context was available for this approved tag.";

      return `## Recommendation ${index + 1}: ${proposal.hierarchyProposalId}

- Target tag: ${proposal.targetTag.layer}:${proposal.targetTag.slug}
- Target name: ${proposal.targetTag.name}
- Target description: ${proposal.targetTag.description}
- Recommended parents:
${formatMarkdownItems(
  (proposal.recommendedParents ?? []).length > 0
    ? (proposal.recommendedParents ?? []).map(
        (parentTag) =>
          `${parentTag.layer}:${parentTag.slug} | ${parentTag.name} | ${parentTag.description}`,
      )
    : proposal.recommendedParentSlugs,
)}
- Synthesized parent:
${
  proposal.synthesizedParent
    ? `${proposal.synthesizedParent.layer}:${proposal.synthesizedParent.slug}
  - Name: ${proposal.synthesizedParent.name}
  - Description: ${proposal.synthesizedParent.description}
  - Synonyms: ${proposal.synthesizedParent.synonyms.join(", ") || "(none)"}
  - Parent tags: ${
    (proposal.synthesizedParent.parentTags ?? []).length > 0
      ? (proposal.synthesizedParent.parentTags ?? [])
          .map(
            (parentTag) =>
              `${parentTag.layer}:${parentTag.slug} | ${parentTag.name} | ${parentTag.description}`,
          )
          .join("; ")
      : proposal.synthesizedParent.parentSlugs.join(", ") || "(none)"
  }
  - Child proposal ids: ${proposal.synthesizedParent.childProposalIds.join(", ")}`
    : "  - (none)"
}
- Source raw tags:
${formatMarkdownItems(proposal.sourceTagKeys.map((sourceTagKey) => sourceTagKey.rawKey))}
- Shared Tagger ancestors:
${formatMarkdownItems(
  proposal.sharedAncestors.map(
    (ancestor) =>
      `${ancestor.rawName} (${ancestor.rawKey})${ancestor.category ? " [category]" : ""}`,
  ),
)}
- Candidate existing parents:
${formatMarkdownItems(
  (proposal.candidateParents ?? []).length > 0
    ? (proposal.candidateParents ?? []).map(
        (parentTag) =>
          `${parentTag.layer}:${parentTag.slug} | ${parentTag.name} | ${parentTag.description}`,
      )
    : proposal.candidateParentSlugs,
)}
${promptContextSection}
- Recommendation reasoning: ${proposal.reasoningSummary}`;
    })
    .join("\n\n");

  return `# Canonical Hierarchy Awaiting-Approval Review

You are reviewing generated hierarchy recommendations for canonical bootstrap run \`${runId ?? "unknown"}\`.

Hierarchy run: \`${hierarchyRunId ?? "unknown"}\`

## Required context

Before reviewing any recommendation, read and internalize these repository documents:

- \`docs/product-design.md\`
- \`docs/system-architecture.md\`

Use those documents as governing context for product intent, taxonomy behavior, system constraints, and approval standards.

${SHARED_TAXONOMY_REVIEW_RULES_SECTION}

## Goal

Identify whether any generated hierarchy recommendations should be denied, or should be approved with specific parent removals.

## Guidance

Approve by default if a recommendation creates a broader, useful same-layer parent relationship or synthesized parent tag.

Prefer removing individual parent links instead of denying the whole recommendation when the overall recommendation is useful but one or more suggested parents are weak, noisy, or misleading.

Only deny a recommendation when there is a meaningful problem such as:

- the suggested parent is not actually broader than the child
- the hierarchy relationship is misleading or noisy
- a synthesized parent is too vague, too narrow, or duplicates an existing concept
- the recommendation conflicts with the Tagger ancestry evidence
- the recommendation creates a poor user-facing taxonomy shape

## Required response

- Reply with \`APPROVE_ALL\` if none of the recommendations should be denied or modified.
- Otherwise, reply with a markdown table containing:
  - \`hierarchyProposalId\`
  - \`deny\`
  - \`removeRecommendedParentSlugs\`
  - \`removeSynthesizedParentParentSlugs\`
  - \`reason\`
- Use comma-separated slug lists for the two remove columns, or leave them empty.
- Only set \`deny\` to \`yes\` for recommendations that should be rejected outright.
- Use parent removals when the recommendation should still be approved after dropping specific parent links.
- Keep recommendations out of the table if they should remain fully approved with no changes.

Generated at: ${generatedAt}
Pending hierarchy recommendation count in this file: ${items.length}
${isChunkedExport ? `Total pending hierarchy recommendation count across all files: ${totalItemCount ?? items.length}` : ""}
${isChunkedExport ? `Export part: ${partIndex} of ${partCount}` : ""}

${proposalSections}`;
};

const createZipCrc32Table = () => {
  const table = new Uint32Array(256);

  for (let index = 0; index < 256; index += 1) {
    let value = index;

    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) === 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }

    table[index] = value >>> 0;
  }

  return table;
};

const ZIP_CRC32_TABLE = createZipCrc32Table();

const calculateZipCrc32 = (bytes: Uint8Array) => {
  let crc = 0xffffffff;

  for (let index = 0; index < bytes.length; index += 1) {
    const value = bytes[index] ?? 0;
    crc = ZIP_CRC32_TABLE[(crc ^ value) & 0xff]! ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
};

const createDosTimestamp = (date: Date) => {
  const year = Math.max(1980, date.getFullYear());

  return {
    time:
      ((date.getHours() & 0x1f) << 11) |
      ((date.getMinutes() & 0x3f) << 5) |
      Math.floor(date.getSeconds() / 2),
    date:
      (((year - 1980) & 0x7f) << 9) |
      (((date.getMonth() + 1) & 0x0f) << 5) |
      (date.getDate() & 0x1f),
  };
};

const createZipHeader = (length: number, writer: (view: DataView) => void) => {
  const buffer = new ArrayBuffer(length);
  const view = new DataView(buffer);
  writer(view);
  return new Uint8Array(buffer);
};

export const createMarkdownZipBlob = (
  files: readonly AwaitingApprovalReviewExportFile[],
) => {
  const encoder = new TextEncoder();
  const createdAt = new Date();
  const dosTimestamp = createDosTimestamp(createdAt);
  const localFileParts: Uint8Array[] = [];
  const centralDirectoryParts: Uint8Array[] = [];
  let localOffset = 0;

  for (const file of files) {
    const fileNameBytes = encoder.encode(file.fileName);
    const contentBytes = encoder.encode(file.content);
    const crc32 = calculateZipCrc32(contentBytes);
    const localHeader = createZipHeader(30, (view) => {
      view.setUint32(0, 0x04034b50, true);
      view.setUint16(4, 20, true);
      view.setUint16(6, 0x0800, true);
      view.setUint16(8, 0, true);
      view.setUint16(10, dosTimestamp.time, true);
      view.setUint16(12, dosTimestamp.date, true);
      view.setUint32(14, crc32, true);
      view.setUint32(18, contentBytes.length, true);
      view.setUint32(22, contentBytes.length, true);
      view.setUint16(26, fileNameBytes.length, true);
      view.setUint16(28, 0, true);
    });
    const centralHeader = createZipHeader(46, (view) => {
      view.setUint32(0, 0x02014b50, true);
      view.setUint16(4, 20, true);
      view.setUint16(6, 20, true);
      view.setUint16(8, 0x0800, true);
      view.setUint16(10, 0, true);
      view.setUint16(12, dosTimestamp.time, true);
      view.setUint16(14, dosTimestamp.date, true);
      view.setUint32(16, crc32, true);
      view.setUint32(20, contentBytes.length, true);
      view.setUint32(24, contentBytes.length, true);
      view.setUint16(28, fileNameBytes.length, true);
      view.setUint16(30, 0, true);
      view.setUint16(32, 0, true);
      view.setUint16(34, 0, true);
      view.setUint16(36, 0, true);
      view.setUint32(38, 0, true);
      view.setUint32(42, localOffset, true);
    });

    localFileParts.push(localHeader, fileNameBytes, contentBytes);
    centralDirectoryParts.push(centralHeader, fileNameBytes);
    localOffset +=
      localHeader.length + fileNameBytes.length + contentBytes.length;
  }

  const centralDirectorySize = centralDirectoryParts.reduce(
    (total, part) => total + part.length,
    0,
  );
  const endOfCentralDirectory = createZipHeader(22, (view) => {
    view.setUint32(0, 0x06054b50, true);
    view.setUint16(4, 0, true);
    view.setUint16(6, 0, true);
    view.setUint16(8, files.length, true);
    view.setUint16(10, files.length, true);
    view.setUint32(12, centralDirectorySize, true);
    view.setUint32(16, localOffset, true);
    view.setUint16(20, 0, true);
  });

  return new Blob(
    [...localFileParts, ...centralDirectoryParts, endOfCentralDirectory],
    {
      type: "application/zip",
    },
  );
};

export const buildAwaitingApprovalReviewExportFiles = ({
  runId,
  items,
  drafts,
}: {
  runId?: string;
  items: readonly ICanonicalBootstrapAwaitingApprovalExportProposal[];
  drafts: Readonly<Record<string, CanonicalProposalDraft>>;
}): AwaitingApprovalReviewExportFile[] => {
  const safeRunId = runId ?? "workspace";

  if (items.length <= MAX_PROPOSALS_PER_REVIEW_MARKDOWN) {
    return [
      {
        fileName: `canonical-bootstrap-awaiting-approvals-${safeRunId}.md`,
        content: buildAwaitingApprovalReviewMarkdown({
          runId,
          items,
          drafts,
        }),
      },
    ];
  }

  const partCount = Math.ceil(items.length / MAX_PROPOSALS_PER_REVIEW_MARKDOWN);
  const files: AwaitingApprovalReviewExportFile[] = [
    {
      fileName: "README.md",
      content: `# Canonical Bootstrap Awaiting-Approval Review Export

Run ID: \`${safeRunId}\`
Total pending proposals: ${items.length}
Split into ${partCount} markdown files of up to ${MAX_PROPOSALS_PER_REVIEW_MARKDOWN} proposals each.

Review each markdown part independently with the same approval instructions.
`,
    },
  ];

  for (let partIndex = 0; partIndex < partCount; partIndex += 1) {
    const startIndex = partIndex * MAX_PROPOSALS_PER_REVIEW_MARKDOWN;
    const partItems = items.slice(
      startIndex,
      startIndex + MAX_PROPOSALS_PER_REVIEW_MARKDOWN,
    );
    const partNumber = partIndex + 1;
    files.push({
      fileName: `canonical-bootstrap-awaiting-approvals-${safeRunId}-part-${String(partNumber).padStart(2, "0")}-of-${String(partCount).padStart(2, "0")}.md`,
      content: buildAwaitingApprovalReviewMarkdown({
        runId,
        items: partItems,
        drafts,
        partIndex: partNumber,
        partCount,
        totalItemCount: items.length,
      }),
    });
  }

  return files;
};

export const buildHierarchyAwaitingApprovalReviewExportFiles = ({
  runId,
  hierarchyRunId,
  items,
}: {
  runId?: string;
  hierarchyRunId?: string;
  items: readonly ICanonicalHierarchyAwaitingApprovalExportProposal[];
}): AwaitingApprovalReviewExportFile[] => {
  const safeRunId = runId ?? "workspace";

  if (items.length <= MAX_HIERARCHY_PROPOSALS_PER_REVIEW_MARKDOWN) {
    return [
      {
        fileName: `canonical-hierarchy-awaiting-approvals-${safeRunId}.md`,
        content: buildHierarchyAwaitingApprovalReviewMarkdown({
          runId,
          hierarchyRunId,
          items,
        }),
      },
    ];
  }

  const partCount = Math.ceil(
    items.length / MAX_HIERARCHY_PROPOSALS_PER_REVIEW_MARKDOWN,
  );
  const files: AwaitingApprovalReviewExportFile[] = [
    {
      fileName: "README.md",
      content: `# Canonical Hierarchy Awaiting-Approval Review Export

Run ID: \`${safeRunId}\`
Hierarchy run ID: \`${hierarchyRunId ?? "unknown"}\`
Total pending hierarchy recommendations: ${items.length}
Split into ${partCount} markdown files of up to ${MAX_HIERARCHY_PROPOSALS_PER_REVIEW_MARKDOWN} recommendations each.

Review each markdown part independently with the same approval instructions.
`,
    },
  ];

  for (let partIndex = 0; partIndex < partCount; partIndex += 1) {
    const startIndex = partIndex * MAX_HIERARCHY_PROPOSALS_PER_REVIEW_MARKDOWN;
    const partItems = items.slice(
      startIndex,
      startIndex + MAX_HIERARCHY_PROPOSALS_PER_REVIEW_MARKDOWN,
    );
    const partNumber = partIndex + 1;
    files.push({
      fileName: `canonical-hierarchy-awaiting-approvals-${safeRunId}-part-${String(partNumber).padStart(2, "0")}-of-${String(partCount).padStart(2, "0")}.md`,
      content: buildHierarchyAwaitingApprovalReviewMarkdown({
        runId,
        hierarchyRunId,
        items: partItems,
        partIndex: partNumber,
        partCount,
        totalItemCount: items.length,
      }),
    });
  }

  return files;
};
